import React, { useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { analyzeSigePlanDocument } from '../../services/geminiService';
import { Upload, X, Bot, CheckSquare, Square, Loader2 } from 'lucide-react';
import { Button } from '../Button';
import { AI_PROMPTS } from './hooks/ki-prompts';
import { baueKiZeilen } from './uebernahme';

interface DokumentenUploadProps {
  sigeProjektId: string;
  /** Projekt-Bauzeit — Zeiträume übernommener Schritte werden darauf geklemmt. */
  projektVon?: string;
  projektBis?: string;
  /** Plan ohne Bauzeitenplan: übernommene Schritte bekommen keine Zeiträume. */
  ohneZeitachse?: boolean;
  /** Sortierung der übernommenen Schritte setzt hinter den bestehenden auf. */
  startSortierung?: number;
  /** Bereits bekannte Hauptabschnitte (Standard + projektspezifisch). */
  hauptabschnitte?: string[];
  /** Bezeichnungen im Plan — verhindert doppelte Anlage derselben Arbeit. */
  vorhandeneBezeichnungen?: string[];
  onClose: () => void;
  onResultsApplied: () => void;
}

export const DokumentenUpload: React.FC<DokumentenUploadProps> = ({
  sigeProjektId, projektVon, projektBis, ohneZeitachse = false, startSortierung = 0, hauptabschnitte, vorhandeneBezeichnungen,
  onClose, onResultsApplied
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any | null>(null);
  const [selectedSchritte, setSelectedSchritte] = useState<number[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFehler(null);
      setFile(e.target.files[0]);
    }
  };

  /**
   * Datei als base64 lesen. Der FileReader wird in ein Promise gekapselt —
   * früher lief `finally { setLoading(false) }` sofort ab und ein Fehler im
   * asynchronen `onload` erreichte den catch-Zweig nie: der Nutzer sah einen
   * fertigen Dialog ohne Ergebnis und ohne Fehlermeldung.
   */
  const leseBase64 = (datei: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const teil = (reader.result as string).split(',')[1];
        if (!teil) reject(new Error('Die Datei konnte nicht gelesen werden (leerer Inhalt).'));
        else resolve(teil);
      };
      reader.onerror = () => reject(reader.error || new Error('Die Datei konnte nicht gelesen werden.'));
      reader.onabort = () => reject(new Error('Das Einlesen der Datei wurde abgebrochen.'));
      reader.readAsDataURL(datei);
    });

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setFehler(null);
    try {
      const base64Data = await leseBase64(file);

      // Proxy zum Gemini-Backend: Dokument + Prompt
      const res = await analyzeSigePlanDocument(base64Data, file.type, AI_PROMPTS.DOKUMENT_ANALYSE);

      let jsonRes = res;

      // Handle wrapped responses from the backend
      if (jsonRes && jsonRes.text) {
        jsonRes = jsonRes.text;
      }
      if (jsonRes && jsonRes.content) {
        jsonRes = jsonRes.content;
      }

      if (typeof jsonRes === 'string') {
        const cleaned = jsonRes.replace(/```json/g, '').replace(/```/g, '').trim();
        try {
          jsonRes = JSON.parse(cleaned);
        } catch {
          throw new Error('Die Antwort der KI war nicht auswertbar. Bitte erneut versuchen.');
        }
      }

      setResults(jsonRes);
      if (jsonRes?.arbeitsschritte?.length > 0) {
        setSelectedSchritte(jsonRes.arbeitsschritte.map((_: any, i: number) => i));
      }
    } catch (e: any) {
      console.error('Dokument-Analyse fehlgeschlagen', e);
      setFehler(`Analyse fehlgeschlagen: ${e?.message || String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleSchritt = (index: number) => {
     if (selectedSchritte.includes(index)) {
       setSelectedSchritte(selectedSchritte.filter(i => i !== index));
     } else {
       setSelectedSchritte([...selectedSchritte, index]);
     }
  };

  const handleApply = async () => {
     if (!results || !results.arbeitsschritte) return;
     setLoading(true);
     setFehler(null);
     try {
       const stepsToApply = results.arbeitsschritte.filter((_: any, i: number) => selectedSchritte.includes(i));

       // Gemeinsame Übernahme-Regeln (uebernahme.ts): Hauptabschnitt ableiten,
       // fortlaufend sortieren, Zeiträume auf die Bauzeit klemmen und bereits
       // vorhandene Bezeichnungen auslassen. Ohne das landeten übernommene
       // KI-Schritte unsortiert unter „Ohne Abschnitt".
       const { rows, duplikate } = baueKiZeilen(stepsToApply, {
         sigeProjektId,
         zeitraum: { von: projektVon, bis: projektBis },
         ohneZeitraeume: ohneZeitachse,
         startSortierung,
         bekannteAbschnitte: hauptabschnitte,
         vorhandeneBezeichnungen,
       });

       if (rows.length === 0) {
         setFehler(
           duplikate.length > 0
             ? 'Alle ausgewählten Vorschläge stehen bereits im Plan, nichts übernommen.'
             : 'Keiner der ausgewählten Vorschläge hat eine Bezeichnung, nichts übernommen.',
         );
         return;
       }

       const { error } = await supabase.from('sige_arbeitsschritte').insert(rows);
       if (error) throw error;
       onResultsApplied();
       onClose();
     } catch (e: any) {
       console.error('Übernahme der KI-Arbeitsschritte fehlgeschlagen', e);
       setFehler(`Übernahme fehlgeschlagen: ${e?.message || String(e)}`);
     }
     finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            Dokument analysieren
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {fehler && (
          <div className="p-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex justify-between items-center gap-2">
            <span>{fehler}</span>
            <button onClick={() => setFehler(null)} className="text-red-400 hover:text-red-600 px-1 shrink-0">✕</button>
          </div>
        )}

        <div className="p-6 flex-1 overflow-y-auto">
          {!results ? (
             <div className="space-y-6">
               <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors">
                  <Upload className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                  <p className="text-slate-700 font-medium mb-1">Datei hierher ziehen oder auswählen</p>
                  <p className="text-sm text-slate-500 mb-4">PDF, XLSX, DOCX (max 20 MB)</p>
                  <input type="file" onChange={handleFileChange} className="hidden" id="file_upload" />
                  <label htmlFor="file_upload" className="bg-white border border-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 cursor-pointer shadow-sm">
                    Datei auswählen
                  </label>
                  {file && <div className="mt-4 text-sm font-medium text-blue-600">{file.name}</div>}
               </div>

               <div className="flex justify-end">
                  <Button onClick={handleAnalyze} disabled={!file || loading} className="w-full sm:w-auto">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                    {loading ? 'Analysiere...' : 'Analyse starten'}
                  </Button>
               </div>
             </div>
          ) : (
             <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Gefundene Arbeitsschritte</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto p-1">
                    {(!results.arbeitsschritte || results.arbeitsschritte.length === 0) ? (
                      <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        Keine Arbeitsschritte im Dokument gefunden oder KI-Antwort unerwartet: {JSON.stringify(results)}
                      </div>
                    ) : (
                      results.arbeitsschritte.map((step: any, idx: number) => {
                         const isSelected = selectedSchritte.includes(idx);
                         return (
                           <div 
                             key={idx} 
                             onClick={() => toggleSchritt(idx)}
                             className={`p-3 border rounded-xl flex items-start gap-3 cursor-pointer transition-colors ${
                               isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                             }`}
                           >
                             <div className="mt-0.5">
                               {isSelected ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-slate-300" />}
                             </div>
                             <div>
                               <div className="font-medium text-slate-800 text-sm">{step.bezeichnung} <span className="text-slate-400 text-xs ml-2">{((step.konfidenz || 0) * 100).toFixed(0)}% relevant</span></div>
                               <div className="text-xs text-slate-500 mt-1">Gewerk: {step.gewerk}</div>
                             </div>
                           </div>
                         )
                      })
                    )}
                  </div>
                </div>

                {results.hinweise && results.hinweise.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <h4 className="text-sm font-medium text-amber-800 mb-2">KI-Hinweise</h4>
                    <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                      {results.hinweise.map((h: string, i: number) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                )}
             </div>
          )}
        </div>

        {results && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
             <Button variant="ghost" onClick={() => setResults(null)}>Zurück</Button>
             <Button variant="primary" onClick={handleApply} disabled={selectedSchritte.length === 0 || loading}>
               {loading ? 'Wird gespeichert...' : `Ausgewählte übernehmen (${selectedSchritte.length})`}
             </Button>
          </div>
        )}
      </div>
    </div>
  );
};
